/**
 * 对话日志分区（i18n 命名空间思路）
 *
 * 独立文件维护：请求体分块（系统提示 / 状态栈 / 插入的消息 / 工具调用 / 工具返回 / 思考 / 收尾报错…）、
 * 一轮对话的结局标签（正常 / 无输出 / 被收尾 / 报错）与状态帧身份标签。
 * 与 tool.ts、admin_config.ts 同模式：translations.ts 运行时合并，前端用 t('logs:xxx') 取，
 * key 在分区内不重复写前缀。
 */

import type { TranslationDict } from '../types'

export const logsZh: TranslationDict = {
  latestRequest: '最新一次请求体',
  empty: '这条日志没有正文。',
  viewRaw: '原始 JSON',
  viewSegments: '分段视图',
  kindSystem: '系统提示',
  kindState: '状态栈',
  kindInjected: '插入的消息',
  kindUser: '收到的话',
  kindAssistant: 'AI 说的话',
  kindRoundTools: '本轮工具',
  kindToolCall: '工具调用',
  kindToolResult: '工具返回',
  kindReasoning: '思考',
  kindError: '收尾/报错',
  statusOk: '正常',
  statusNoOutput: '无输出',
  statusWrapup: '被收尾',
  statusError: '报错',
  stateNone: '无状态',
  stateTypeGroup: '群',
  stateTypeDm: '私信',
  stateTypeWorld: '世界',
  mentionUnknown: '用户{id}',
  noLogs: '还没有日志。',
  loadFailed: '日志加载失败。',
  blockRaw: '原文',
  blockRender: '渲染',
  blockSourceHint: '看这条消息的逐字原文（库里存的那份，不经过渲染）',
  downloadJson: '下载 JSON',
  downloadMd: '下载 Markdown',
  downloadHtml: '下载 HTML',
  downloadHtmlPreparing: '正在准备…',
  downloadHtmlHint: '把当前渲染结果存成可直接打开的 HTML 文件',
  stateBack: '返回状态列表',
  stateRequests: '条请求',
  stateHistory: '这段状态的历史',
  bodyFull: '完整',
  bodyDelta: '改变量',
  deltaFirst: '这是这段状态的第一条请求，没有可比的上一条。',
  deltaSummary: '与上一条 #{prev} 相比：新增 {added} 条、消失 {removed} 条、相同 {shared} 条',
  hunkAdded: '新增 {n} 条',
  hunkRemoved: '消失 {n} 条',
  hunkSame: '相同 {n} 条',
}

export const logsEn: TranslationDict = {
  latestRequest: 'Latest request body',
  empty: 'This log has no content.',
  viewRaw: 'Raw JSON',
  viewSegments: 'Segmented view',
  kindSystem: 'System prompt',
  kindState: 'State stack',
  kindInjected: 'Injected message',
  kindUser: 'Incoming message',
  kindAssistant: 'AI reply',
  kindRoundTools: 'This round tools',
  kindToolCall: 'Tool call',
  kindToolResult: 'Tool result',
  kindReasoning: 'Reasoning',
  kindError: 'Wrap-up / error',
  statusOk: 'OK',
  statusNoOutput: 'No output',
  statusWrapup: 'Wrapped up',
  statusError: 'Error',
  stateNone: 'No state',
  stateTypeGroup: 'Group',
  stateTypeDm: 'DM',
  stateTypeWorld: 'World',
  mentionUnknown: 'user {id}',
  noLogs: 'No logs yet.',
  loadFailed: 'Failed to load logs.',
  blockRaw: 'Raw',
  blockRender: 'Rendered',
  blockSourceHint: 'Show this message exactly as stored, without rendering',
  downloadJson: 'Download JSON',
  downloadMd: 'Download Markdown',
  downloadHtml: 'Download HTML',
  downloadHtmlPreparing: 'Preparing…',
  downloadHtmlHint: 'Save the rendered view as a standalone HTML file',
  stateBack: 'Back to states',
  stateRequests: 'requests',
  stateHistory: 'History of this state',
  bodyFull: 'Full',
  bodyDelta: 'Changes',
  deltaFirst: 'This is the first request of this state — nothing to compare with.',
  deltaSummary: 'vs #{prev}: {added} added, {removed} gone, {shared} unchanged',
  hunkAdded: '{n} added',
  hunkRemoved: '{n} gone',
  hunkSame: '{n} unchanged',
}

export const logsJa: TranslationDict = {
  latestRequest: '最新のリクエストボディ',
  empty: 'このログには本文がありません。',
  viewRaw: '生の JSON',
  viewSegments: 'セグメント表示',
  kindSystem: 'システムプロンプト',
  kindState: '状態スタック',
  kindInjected: '挿入されたメッセージ',
  kindUser: '受信した発言',
  kindAssistant: 'AI の発言',
  kindRoundTools: '今回のツール',
  kindToolCall: 'ツール呼び出し',
  kindToolResult: 'ツール応答',
  kindReasoning: '思考',
  kindError: '終了・エラー',
  statusOk: '正常',
  statusNoOutput: '出力なし',
  statusWrapup: '打ち切り',
  statusError: 'エラー',
  stateNone: '状態なし',
  stateTypeGroup: 'グループ',
  stateTypeDm: 'DM',
  stateTypeWorld: '世界',
  mentionUnknown: 'ユーザー{id}',
  noLogs: 'ログがまだありません。',
  loadFailed: 'ログの読み込みに失敗しました。',
  blockRaw: '原文',
  blockRender: 'レンダリング',
  blockSourceHint: '保存されているままの原文を表示（レンダリングなし）',
  downloadJson: 'JSON をダウンロード',
  downloadMd: 'Markdown をダウンロード',
  downloadHtml: 'HTML をダウンロード',
  downloadHtmlPreparing: '準備中…',
  downloadHtmlHint: '現在のレンダリング結果を単体で開ける HTML として保存',
  stateBack: '状態一覧へ戻る',
  stateRequests: '件のリクエスト',
  stateHistory: 'この状態の履歴',
  bodyFull: '全体',
  bodyDelta: '変更点',
  deltaFirst: 'この状態の最初のリクエストです。比較対象がありません。',
  deltaSummary: '#{prev} との比較：追加 {added}、消失 {removed}、同一 {shared}',
  hunkAdded: '追加 {n}',
  hunkRemoved: '消失 {n}',
  hunkSame: '同一 {n}',
}
